import { Component, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { TranslateService } from '@ngx-translate/core';
import { ChannelService } from '@service/channel/channel.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { RunningHourGroupSocketService } from '@service/running-hour-group-socket.service';
import { RunningHourService } from '@service/running-hour-group.service';
import { VesselService } from '@service/vessel.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType } from 'icreate/common/models/channel-type';
import { RunningHourGroup } from 'icreate/common/models/running-hour-group';
import { Vessel } from 'icreate/common/models/vessel';
import _ from 'lodash';
import { debounceTime, lastValueFrom, Subject, take, takeUntil } from 'rxjs';

@Component({
    selector: 'app-running-hour-setting',
    templateUrl: './running-hour-setting.component.html',
    styleUrl: './running-hour-setting.component.css',
    standalone: false,
})
export class RunningHourSettingComponent {
    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean = true;
    @Output() pageUpdated = new EventEmitter<boolean>();

    @ViewChild('autoSetConfirmation') autoSetConfirmation: ConfirmationWindowComponent;

    isSelectAll: boolean = false;
    isLoading: boolean = true;
    isAutoSetting: boolean = false;
    vesselId: string;
    isUpdated: boolean = false;

    runningHourGroups: RunningHourGroup[] = [];
    initialRunningHourGroups: RunningHourGroup[] = [];
    dataSourceChannelMap: Map<string, Channel> = new Map();
    runningHourChannelMap: Map<string, Channel> = new Map();

    configuredRunningHourGroups: RunningHourGroup[] = [];

    private destroySubscriptionSubject$ = new Subject<void>();
    searchInput: string = '';
    filteredRunningHourGroups: any[] = [];

    isFilteringIncomplete: boolean = false;

    errorMessage: string;
    openRowIndex: string | null = null;

    constructor(
        private runningHourGroupSocketService: RunningHourGroupSocketService,
        private runningHourGroupService: RunningHourService,
        private translate: TranslateService,
        private messageDialogService: MessageDialogService,
        private vesselService: VesselService,
        private channelService: ChannelService
    ) {}

    @HostListener('document:click')
    onDocumentClick(): void {
        this.closeDropdown();
    }

    async ngOnInit() {
        this.vesselId = this.vesselService.getActiveVesselId();

        this.runningHourGroupSocketService
            .runningHourGroupUpdated$()
            .pipe(debounceTime(100), takeUntil(this.destroySubscriptionSubject$))
            .subscribe(updatedRunningHourGroup => {
                if (updatedRunningHourGroup.vesselId === this.vesselId) {
                    this.runningHourGroups = this.runningHourGroups.map(runningHourGroup => {
                        if (runningHourGroup.id === updatedRunningHourGroup.id) {
                            return updatedRunningHourGroup;
                        } else {
                            return runningHourGroup;
                        }
                    });
                }
                this.messageDialogService.openMessage(
                    this.translate.instant('runnning-hour-group.message-dialog.update.message')
                );
                this.isUpdated = false;
            });

        this.runningHourGroups = await this.runningHourGroupService.getAllGroups(this.vesselId);
        this.initialRunningHourGroups = _.cloneDeep(this.runningHourGroups);

        for (const data of this.runningHourGroups) {
            this.runningHourChannelMap.set(data.id, data.channel);
            this.dataSourceChannelMap.set(data.id, data.dataSourceChannel);
        }

        this.filterRunningHourGroups();
        this.errorMessage = this.translate.instant('running-hour-group.page.no-data-message');
        this.isLoading = false;
    }

    filterRunningHourGroups() {
        this.errorMessage = this.translate.instant('running-hour-group.search.no-result.text');
        const term = (this.searchInput ?? '').trim().toLowerCase();

        if (!term) {
            this.filteredRunningHourGroups = [...this.runningHourGroups].sort((a, b) =>
                a?.channel?.fcuData?.fcuChannelId?.localeCompare(b?.channel?.fcuData?.fcuChannelId, ['en', 'jp'], {
                    numeric: true,
                    sensitivity: 'variant',
                })
            );
            return;
        }

        this.filteredRunningHourGroups = this.runningHourGroups
            .filter(group => {
                if (this.isFilteringIncomplete && this.checkStatusSet(group)) {
                    return false;
                }
                const channel = this.runningHourChannelMap.get(group.id);
                const channelName = channel?.opsData?.name?.toLowerCase() || '';
                return channelName.includes(term);
            })
            .sort((a, b) =>
                a?.channel?.fcuData?.fcuChannelId?.localeCompare(b?.channel?.fcuData?.fcuChannelId, ['en', 'jp'], {
                    numeric: true,
                    sensitivity: 'variant',
                })
            );
    }

    getIncompleteRunningHourGroups() {
        this.isFilteringIncomplete = !this.isFilteringIncomplete;
        this.filteredRunningHourGroups = this.isFilteringIncomplete
            ? this.runningHourGroups.filter(group => !this.checkStatusSet(group))
            : [...this.runningHourGroups];
    }

    async autoSetDataSourceChannelsDialog() {
        if (!this.userCanEdit) return;
        this.autoSetConfirmation.setVisible(true);
        const confirmationOption = await lastValueFrom(this.autoSetConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            await this.autoSetDataSourceChannels();
        }
    }

    async autoSetDataSourceChannels() {
        this.isAutoSetting = true;

        const channels = await lastValueFrom(this.channelService.getChannels(this.vesselId));
        const motorChannelMap: Map<string, Channel> = new Map();
        const channelByIdMap: Map<string, Channel> = new Map();
        for (const channel of channels) {
            channelByIdMap.set(channel.id, channel);
            if (channel.fcuData?.channelType !== ChannelType.DIGITAL_MOTOR) continue;
            const fuAddress = this.getFuAddressKey(channel);
            if (!fuAddress || motorChannelMap.has(fuAddress)) continue;
            motorChannelMap.set(fuAddress, channel);
        }

        let assignedCount = 0;
        for (const group of this.runningHourGroups) {
            const assignedChannel = group.dataSouceChannelId ? channelByIdMap.get(group.dataSouceChannelId) : null;
            if (group.dataSouceChannelId && !assignedChannel) continue;
            if (assignedChannel && assignedChannel.fcuData?.channelType !== ChannelType.DIGITAL_MOTOR) continue;

            const runningHourChannel =
                channelByIdMap.get(group.runningHourChannelId) ?? this.runningHourChannelMap.get(group.id);
            const fuAddress = this.getFuAddressKey(runningHourChannel);
            const isAssignedMatched = assignedChannel && this.getFuAddressKey(assignedChannel) === fuAddress;
            const matchedChannel = fuAddress ? motorChannelMap.get(fuAddress) : null;
            const motorChannel = isAssignedMatched ? assignedChannel : matchedChannel;

            if (!motorChannel) {
                if (!assignedChannel) continue;
                group.dataSouceChannelId = null;
                group.statusOn = false;
                group.statusOff = false;
                group.motorChValue = null;
                this.dataSourceChannelMap.set(group.id, null);
                assignedCount++;
                continue;
            }

            group.dataSouceChannelId = motorChannel.id;
            group.statusOn = false;
            group.statusOff = false;
            group.motorChValue = this.getMotorRunStatusValue(motorChannel);
            this.dataSourceChannelMap.set(group.id, motorChannel);
            assignedCount++;
        }

        this.filterRunningHourGroups();
        this.onGroupUpdated();
        this.isAutoSetting = false;

        this.messageDialogService.openMessage(
            assignedCount > 0
                ? 'runnning-hour-group.message-dialog.update.message'
                : 'running-hour-group.auto-set.no-match.message'
        );
    }

    private getMotorRunStatusValue(channel: Channel): number | null {
        const motorChannelSetting = channel.fcuData?.motorChannelSetting;
        if (!motorChannelSetting) return null;

        let motorChValue = 0;
        for (let i = 1; i <= 4; i++) {
            if (motorChannelSetting[`runStatusString${i}`]) motorChValue += 1 << i;
        }
        return motorChValue || null;
    }

    private getFuAddressKey(channel: Channel): string | null {
        const fuInputAddress = channel?.fcuData?.inputData?.fuInputAddress;
        const memoryInputAddress = channel?.fcuData?.inputData?.memoryInputAddress;

        const isFuAddressValid = fuInputAddress && (fuInputAddress.fuNo || fuInputAddress.slotNo || fuInputAddress.pin);
        const isMemoryAddressValid = memoryInputAddress?.address;

        if (!isFuAddressValid && !isMemoryAddressValid) return null;

        const fuKey = isFuAddressValid
            ? [
                  channel.fcuData.fcuNo,
                  fuInputAddress.fuNo ?? 0,
                  fuInputAddress.slotNo ?? 0,
                  fuInputAddress.pin ?? 0,
              ].join('-')
            : '';
        const memoryKey = isMemoryAddressValid ? memoryInputAddress.address.toString() : '';

        return [fuKey, memoryKey].join('|');
    }

    checkStatusSet(runningHourGroup: RunningHourGroup) {
        return runningHourGroup.statusOn || runningHourGroup.statusOff || runningHourGroup.motorChValue != null;
    }

    getIsGroupUpdated() {
        return this.isUpdated;
    }

    async save() {
        if (!this.userCanEdit) return;
        this.isLoading = true;
        try {
            await this.runningHourGroupService.saveAll(this.runningHourGroups);
        } catch (error) {
            this.messageDialogService.openMessage(
                this.translate.instant('runnning-hour-group.message-dialog.save.error-message')
            );
        }
        this.isLoading = false;
    }

    onGroupUpdated() {
        const hasChanges = this.userCanEdit && !_.isEqual(this.initialRunningHourGroups, this.runningHourGroups);

        this.pageUpdated.emit(hasChanges);
    }

    onDataSourceChannelChanged(event: { groupId: string; channel: Channel }) {
        this.dataSourceChannelMap.set(event.groupId, event.channel);
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    showMessageDialog(message, name) {
        this.messageDialogService.openMessage(message, { name: name });
    }

    get isShowTable() {
        return this.filteredRunningHourGroups.length > 0;
    }

    toggleDropdown(index: string): void {
        this.openRowIndex = this.openRowIndex === index ? null : index;
    }

    closeDropdown(): void {
        this.openRowIndex = null;
    }
}
